import React, { useEffect, useState } from "react";
import { Form, Button, Alert, ProgressBar } from "react-bootstrap";
import { getProfile, updateProfile, getProfileCompletion } from "../services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function BusinessProfile() {
  const [form, setForm] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    getProfile().then(({ data }) => setForm(data.data));
    getProfileCompletion().then(({ data }) => setCompletion(data.data));
  };

  useEffect(load, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    setSaving(true);
    try {
      await updateProfile({
        businessName: form.businessName,
        ownerName: form.ownerName,
        city: form.city,
        address: form.address,
        gstNumber: form.gstNumber,
        panNumber: form.panNumber,
        serviceDestinations: (form.serviceDestinations || []).join(","),
        lat: form.location?.lat ?? "",
        lng: form.location?.lng ?? "",
        availabilityDays: (form.availabilityDays || []).join(","),
        operatingHours: form.operatingHours || "",
      });
      setSuccess("Profile updated");
      load();
    } catch (err) {
      setError(getErr(err, "Failed to update profile"));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p>Loading profile…</p>;

  return (
    <div>
      <h4 className="mb-3">Business Profile</h4>

      {completion && (
        <div className="partner-card mb-4">
          <div className="d-flex justify-content-between mb-2">
            <span>Profile completion</span>
            <strong>{completion.percent}%</strong>
          </div>
          <ProgressBar now={completion.percent} />
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form onSubmit={handleSave} className="partner-card">
        <Form.Group className="mb-3">
          <Form.Label>Business Name</Form.Label>
          <Form.Control value={form.businessName || ""} onChange={(e) => setForm({ ...form, businessName: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Owner / Contact Person</Form.Label>
          <Form.Control value={form.ownerName || ""} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Email (login)</Form.Label>
          <Form.Control value={form.email || ""} disabled />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>City</Form.Label>
          <Form.Control value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Address</Form.Label>
          <Form.Control as="textarea" rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>GST Number</Form.Label>
          <Form.Control value={form.gstNumber || ""} onChange={(e) => setForm({ ...form, gstNumber: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>PAN Number</Form.Label>
          <Form.Control value={form.panNumber || ""} onChange={(e) => setForm({ ...form, panNumber: e.target.value })} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Service Destinations (comma separated)</Form.Label>
          <Form.Control
            value={(form.serviceDestinations || []).join(", ")}
            onChange={(e) => setForm({ ...form, serviceDestinations: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Location (for map-based discovery)</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              type="number" step="any" placeholder="Latitude"
              value={form.location?.lat ?? ""}
              onChange={(e) => setForm({ ...form, location: { ...form.location, lat: e.target.value } })}
            />
            <Form.Control
              type="number" step="any" placeholder="Longitude"
              value={form.location?.lng ?? ""}
              onChange={(e) => setForm({ ...form, location: { ...form.location, lng: e.target.value } })}
            />
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Days Open</Form.Label>
          <div className="d-flex flex-wrap gap-3">
            {DAYS.map((day) => (
              <Form.Check
                key={day}
                type="checkbox"
                label={day}
                checked={(form.availabilityDays || []).includes(day)}
                onChange={(e) => {
                  const current = form.availabilityDays || [];
                  const next = e.target.checked ? [...current, day] : current.filter((d) => d !== day);
                  setForm({ ...form, availabilityDays: next });
                }}
              />
            ))}
          </div>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Operating Hours</Form.Label>
          <Form.Control
            placeholder="e.g. 9:00 AM - 8:00 PM"
            value={form.operatingHours || ""}
            onChange={(e) => setForm({ ...form, operatingHours: e.target.value })}
          />
        </Form.Group>
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save Changes"}</Button>
      </Form>
    </div>
  );
}
